import { queryOptions, type QueryClient } from '@tanstack/react-query'
import { SessionDocument, type SessionQuery } from '@/graphql/generated'
import { graphqlRequest } from '@/lib/api/graphql'
import { keys } from '@/lib/queries/keys'
import { fullName, initialsOf, memberRole, nodes } from '@/lib/queries/mappers'
import type { CurrentUser, MemberRole, Organization } from '@/lib/types'

export type SessionData = {
  user: CurrentUser
  organizations: Organization[]
  /** The signed-in user's role in each organization, by organization id. */
  roles: Record<string, MemberRole>
}

/**
 * pg_graphql's shape -> what the shell needs.
 *
 * Named and exported rather than inlined into `select` because `select` runs
 * only on READ — `queryClient.fetchQuery` hands back the raw query data. A
 * Server Component that needs the mapped form has to apply this itself, which
 * `fetchSession` below does.
 */
export function selectSession(data: SessionQuery): SessionData {
  const account = nodes(data.usersCollection)[0]
  const memberships = nodes(data.organizationMembersCollection)

  const name = fullName(account)
  const email = account?.emailAddress ?? ''

  const organizations: Organization[] = []
  const roles: Record<string, MemberRole> = {}

  for (const membership of memberships) {
    const organization = membership.organization
    /* A membership whose organization is invisible is not an error: RLS can
       legitimately hide the row while the link survives. Skipping it beats
       rendering a switcher entry that navigates nowhere. */
    if (!organization) continue

    organizations.push({
      id: organization.id,
      slug: organization.slug,
      name: organization.name,
      plan: organization.organizationType?.name ?? 'Personal Account',
      planCode: organization.organizationType?.code ?? 'personal',
      memberCount: nodes(organization.organizationMembersCollection).length,
    })

    roles[organization.id] = memberRole(membership.organizationMemberRole?.code)
  }

  /* Stable order so the switcher does not reshuffle between renders — the API
     returns membership order, which is creation order. */
  organizations.sort((a, b) => a.name.localeCompare(b.name))

  return {
    user: {
      id: account?.id ?? '',
      name,
      firstName: account?.firstName ?? '',
      lastName: account?.lastName ?? '',
      email,
      initials: initialsOf(name, email),
    },
    organizations,
    roles,
  }
}

/**
 * Who is signed in and where they can go.
 *
 * Everything org-scoped depends on this, so it is fetched once per navigation
 * and kept longer than the default: the answer changes only when someone is
 * added to or removed from an organization.
 */
export function sessionQuery(userId: string) {
  return queryOptions({
    queryKey: keys.session(),
    queryFn: ({ signal }) =>
      graphqlRequest(SessionDocument, { userId }, { signal }),
    staleTime: 5 * 60_000,
    select: selectSession,
  })
}

/**
 * The mapped session, for Server Components.
 *
 * Populates the same cache entry the client will hydrate, so the browser does
 * not refetch what the server already has.
 */
export async function fetchSession(
  queryClient: QueryClient,
  userId: string,
): Promise<SessionData> {
  return selectSession(await queryClient.fetchQuery(sessionQuery(userId)))
}
